import { useState } from 'react';
import { CreditCard, Check, Zap, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { createCheckoutSession, createBillingPortalSession } from '@traction/api-client';
import { PLAN_CONFIGS, type PlanTier } from '@traction/shared';
import { getStripe, STRIPE_PRICES, isStripeConfigured } from '../../lib/stripe';

type BillingInterval = 'monthly' | 'yearly';

const PLAN_ORDER: PlanTier[] = ['starter', 'growth', 'scale'];

export function SubscriptionPage() {
  const { user } = useAuth();
  const { subscription, tier, isActive } = useSubscription();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Calculate days remaining for trial
  const getDaysRemaining = () => {
    if (!subscription?.trial_ends_at && !subscription?.current_period_end) {
      return 14; // Default trial days
    }
    const endDate = subscription.trial_ends_at || subscription.current_period_end;
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const daysRemaining = getDaysRemaining();
  const isOnTrial = tier === 'trial' || subscription?.status === 'trialing';
  const stripeConfigured = isStripeConfigured();

  const handleUpgrade = async (planTier: PlanTier) => {
    if (!user || loading) return;

    setLoading(planTier);
    setError(null);

    try {
      // Get the price ID for the selected plan and interval
      const priceId = STRIPE_PRICES[planTier as keyof typeof STRIPE_PRICES]?.[billingInterval];

      if (!priceId || priceId.startsWith('price_')) {
        // Placeholder price IDs - Stripe not fully configured yet
        setError('Stripe is not fully configured. Please contact support.');
        return;
      }

      const { url } = await createCheckoutSession(user.uid, priceId);

      if (url) {
        window.location.href = url;
      } else {
        // Fallback to Stripe.js redirect
        const stripe = await getStripe();
        if (stripe) {
          const { sessionId } = await createCheckoutSession(user.uid, priceId);
          await stripe.redirectToCheckout({ sessionId });
        }
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!user || loading) return;

    setLoading('billing');
    setError(null);

    try {
      const { url } = await createBillingPortalSession(user.uid);
      window.location.href = url;
    } catch (err) {
      console.error('Billing portal error:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setLoading(null);
    }
  };

  const getPlanPrice = (planTier: PlanTier) => {
    const config = PLAN_CONFIGS[planTier];
    if (billingInterval === 'yearly') {
      const monthlyEquivalent = Math.round(config.price_yearly / 12);
      return { price: monthlyEquivalent, period: '/mo', savings: config.price_monthly * 12 - config.price_yearly };
    }
    return { price: config.price_monthly, period: '/mo', savings: 0 };
  };

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Subscription</h1>
      <p className="mt-1 text-sm text-cacao-muted">Manage your plan and billing.</p>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="mt-6 space-y-6">
        {/* Current Plan Status */}
        <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-medium text-[#F5F5F5]">Current Plan</h3>
              <p className="mt-1 text-2xl font-semibold text-cacao-gold">
                {PLAN_CONFIGS[tier]?.name || 'Free Trial'}
              </p>
              {isOnTrial ? (
                <p className="mt-1 text-sm text-cacao-muted">
                  {daysRemaining > 0 ? `${daysRemaining} days remaining in trial` : 'Trial expired'}
                </p>
              ) : (
                <p className="mt-1 text-sm text-cacao-muted">
                  Status: <span className={isActive ? 'text-green-400' : 'text-red-400'}>
                    {subscription?.status || 'Unknown'}
                  </span>
                </p>
              )}
            </div>
            <div className="rounded-full bg-cacao-gold/10 p-2">
              <CreditCard className="h-5 w-5 text-cacao-gold" />
            </div>
          </div>

          {/* Manage Billing Button for Paid Users */}
          {subscription?.stripe_subscription_id && stripeConfigured && (
            <button
              onClick={handleManageBilling}
              disabled={loading === 'billing'}
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-cacao-dark-tertiary px-4 py-2 text-sm font-medium text-[#F5F5F5] hover:bg-cacao-dark-tertiary transition-colors disabled:opacity-50"
            >
              {loading === 'billing' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Manage Billing
            </button>
          )}
        </div>

        {/* Billing Interval Toggle */}
        <div className="flex items-center justify-center gap-3">
          <span className={`text-sm ${billingInterval === 'monthly' ? 'text-[#F5F5F5]' : 'text-cacao-muted'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingInterval(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
            className="relative h-6 w-11 rounded-full bg-cacao-dark-tertiary transition-colors"
          >
            <div
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-cacao-gold transition-transform ${
                billingInterval === 'yearly' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span className={`text-sm ${billingInterval === 'yearly' ? 'text-[#F5F5F5]' : 'text-cacao-muted'}`}>
            Yearly
            <span className="ml-1 text-xs text-green-400">(Save 20%)</span>
          </span>
        </div>

        {/* Pricing Plans */}
        <div className="grid gap-4 md:grid-cols-3">
          {PLAN_ORDER.map((planTier) => {
            const config = PLAN_CONFIGS[planTier];
            const priceInfo = getPlanPrice(planTier);
            const isCurrentPlan = tier === planTier;
            const isPopular = planTier === 'growth';

            return (
              <div
                key={planTier}
                className={`relative rounded-lg border p-5 ${
                  isPopular
                    ? 'border-cacao-gold bg-cacao-gold/5'
                    : 'border-cacao-dark-tertiary bg-cacao-dark-secondary'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cacao-gold px-3 py-0.5 text-xs font-medium text-cacao-dark">
                    Most Popular
                  </div>
                )}

                <h3 className="text-lg font-medium text-[#F5F5F5]">{config.name}</h3>

                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-[#F5F5F5]">${priceInfo.price}</span>
                  <span className="text-sm text-cacao-muted">{priceInfo.period}</span>
                </div>

                {priceInfo.savings > 0 && (
                  <p className="mt-1 text-xs text-green-400">
                    Save ${priceInfo.savings}/year
                  </p>
                )}

                <ul className="mt-4 space-y-2">
                  {config.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-cacao-muted">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(planTier)}
                  disabled={isCurrentPlan || loading === planTier || !stripeConfigured}
                  className={`mt-5 w-full rounded-md px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                    isCurrentPlan
                      ? 'bg-cacao-dark-tertiary text-cacao-muted cursor-not-allowed'
                      : isPopular
                      ? 'bg-cacao-gold text-cacao-dark hover:bg-cacao-gold-hover'
                      : 'bg-cacao-dark-tertiary text-[#F5F5F5] hover:bg-cacao-dark-tertiary/80'
                  }`}
                >
                  {loading === planTier ? (
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    'Upgrade'
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Coming Soon Banner - Shown when Stripe not configured */}
        {!stripeConfigured && (
          <div className="rounded-lg border border-cacao-gold/30 bg-cacao-gold/5 p-4">
            <div className="flex items-center gap-2 text-sm text-cacao-gold">
              <Zap className="h-4 w-4" />
              <span className="font-medium">Payment Processing Coming Soon</span>
            </div>
            <p className="mt-1 text-xs text-cacao-muted">
              We're finalizing our payment system. Your trial gives you full access while we set this up.
            </p>
          </div>
        )}

        {/* Trial CTA for Trial Users */}
        {isOnTrial && (
          <div className="rounded-lg border border-cacao-gold/30 bg-cacao-gold/5 p-5">
            <div className="flex items-center gap-3 mb-3">
              <Zap className="h-5 w-5 text-cacao-gold" />
              <h3 className="text-sm font-medium text-[#F5F5F5]">Ready to grow?</h3>
            </div>
            <p className="text-sm text-cacao-muted mb-4">
              Upgrade to a paid plan to continue using Traction Bee after your trial ends.
              Your data and settings will be preserved.
            </p>
          </div>
        )}

        {/* What's Included */}
        <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5">
          <h3 className="text-sm font-medium text-[#F5F5F5] mb-4">What's included in your {PLAN_CONFIGS[tier]?.name || 'plan'}</h3>
          <ul className="grid gap-3 sm:grid-cols-2">
            {(PLAN_CONFIGS[tier]?.features || PLAN_CONFIGS.trial.features).map((feature, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-sm text-cacao-muted">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
